<template>
	<div class="third-party-auth">
		<div class="display-flex align-items-center my-125">
			<hr :class="['divider', dividerClass]" />
			<span :class="['align-self-center body-sm font-weight-bold mx-100', textClass]">OR</span>
			<hr :class="['divider', dividerClass]" />
		</div>

		<div class="button-container">
			<GoogleAuthButton
				:disabled="loading"
				:is-working="props.isWorking && googleAuthSubmitted"
				:theme="props.theme"
				@google-auth-payload="handleGoogleAuthPayload"
			/>
			<AppleAuthButton
				:disabled="loading"
				:is-working="props.isWorking && appleAuthSubmitted"
				:theme="props.theme"
				@apple-auth-payload="handleAppleAuthPayload"
			/>
		</div>

		<div class="overflow-hidden">
			<BasePlaceHolderInline
				v-if="displayDisclaimer"
				:class="{ 'display-block mt-100': loading }"
				:placehold="loading"
				:theme="placeholderTheme"
				width="50rem"
			>
				<span
					:class="['text-align-left body-sm display-block mt-100', disclaimerClass]"
					data-test="signup-disclaimers"
				>
					<span v-for="(link, index) in clientAgreementLinks" :key="`link.title${index}`">
						{{ link.prelude }}
						<BaseLink
							:link="{ target: '_blank', href: link.url }"
							classes="text-color-current-color text-decoration-underline"
							><span v-html="deorphanText(link.title)"></span
						></BaseLink> </span
					>.
				</span>
			</BasePlaceHolderInline>
		</div>
	</div>
</template>

<script lang="ts">
export default {
	name: 'ThirdPartyAuth'
};
</script>

<script setup lang="ts">
import { AppleAuthParameters, GoogleAuthParameters, ThirdPartyAuthTheme } from 'types/third-party-auth';
import { computed, ref } from 'vue';
import AppleAuthButton from '@components/third-party-auth/apple-auth-button.vue';
import { deorphanText } from '@filters/shared-filters';
import { DocumentListItem } from 'types/documents';
import { getFundriseClientAgreementDocuments } from '@api/documents';
import GoogleAuthButton from '@components/third-party-auth/google-auth-button.vue';

interface Props {
	disclaimerClass?: string;
	displayDisclaimer?: boolean;
	isWorking?: boolean;
	theme?: ThirdPartyAuthTheme;
}

interface Emits {
	(e: 'google-auth-payload', value: GoogleAuthParameters): void;
	(e: 'apple-auth-payload', value: AppleAuthParameters): void;
}

const props = withDefaults(defineProps<Props>(), {
	disclaimerClass: undefined,
	displayDisclaimer: false,
	isWorking: false,
	theme: 'PRIMARY'
});

const emits = defineEmits<Emits>();

const loading = ref(true);
const googleAuthSubmitted = ref(false);
const appleAuthSubmitted = ref(false);

const clientAgreementLinks = ref<Array<DocumentListItem & { prelude: string }>>([]);
const privacyPolicyAcknowledgedVersionId = ref('');
const termsAcknowledgedVersionId = ref('');

const dividerClass = computed((): string => {
	if (props.theme === 'PRIMARY') {
		return 'divider-primary';
	} else if (props.theme === 'SECONDARY') {
		return 'divider-secondary';
	} else {
		return 'divider-tertiary';
	}
});

const textClass = computed((): string => {
	if (props.theme === 'PRIMARY') {
		return 'divider-text-primary';
	} else if (props.theme === 'SECONDARY') {
		return 'divider-text-secondary';
	} else {
		return 'divider-text-tertiary';
	}
});

const disclaimerClass = computed((): string => {
	if (props.disclaimerClass) {
		return props.disclaimerClass;
	} else if (props.theme === 'PRIMARY') {
		return 'text-color-content-secondary';
	} else if (props.theme === 'SECONDARY') {
		return 'muted';
	} else {
		return 'text-color-white';
	}
});

const placeholderTheme = computed((): string => {
	return props.theme === 'PRIMARY' ? 'light' : 'dark';
});

function handleGoogleAuthPayload(payload: GoogleAuthParameters): void {
	googleAuthSubmitted.value = true;
	appleAuthSubmitted.value = false;

	if (props.displayDisclaimer) {
		payload = {
			...payload,
			privacyPolicyAcknowledgedVersionId: privacyPolicyAcknowledgedVersionId.value,
			termsAcknowledgedVersionId: termsAcknowledgedVersionId.value
		};
	}

	emits('google-auth-payload', payload);
}

function handleAppleAuthPayload(payload: AppleAuthParameters): void {
	appleAuthSubmitted.value = true;
	googleAuthSubmitted.value = false;

	if (props.displayDisclaimer) {
		payload = {
			...payload,
			privacyPolicyAcknowledgedVersionId: privacyPolicyAcknowledgedVersionId.value,
			termsAcknowledgedVersionId: termsAcknowledgedVersionId.value
		};
	}

	emits('apple-auth-payload', payload);
}

async function setup(): Promise<void> {
	try {
		if (props.displayDisclaimer) {
			const clientAgreementDocuments = await getFundriseClientAgreementDocuments();

			const agreementIntro = 'By continuing you indicate you have reviewed and agree to the ';
			const privacyPolicyPrelude = ' and ';
			clientAgreementLinks.value = [
				{
					prelude: agreementIntro,
					...clientAgreementDocuments.termsOfService
				},
				{ prelude: privacyPolicyPrelude, ...clientAgreementDocuments.privacyPolicy }
			];
			privacyPolicyAcknowledgedVersionId.value = clientAgreementDocuments.privacyPolicy.documentRevisionId ?? '';
			termsAcknowledgedVersionId.value = clientAgreementDocuments.termsOfService.documentRevisionId ?? '';
		}
	} catch {
	} finally {
		loading.value = false;
	}
}

setup();
</script>

<style lang="scss" scoped>
@use '../../styles/constants/_colors.scss' as *;

.third-party-auth {
	container-type: inline-size;

	.divider-text {
		&-primary {
			color: rgba(21, 21, 19, 0.4);
		}

		&-secondary {
			color: rgba(255, 255, 255, 0.6);
		}

		&-tertiary {
			color: rgba(255, 255, 255, 0.8);
		}
	}

	.divider {
		border: none;
		flex: 1;

		&-primary {
			border-top: 1px solid rgba(21, 21, 19, 0.2);
		}

		&-secondary {
			border-top: 1px solid rgba(255, 255, 255, 0.4);
		}

		&-tertiary {
			border-top: 1px solid rgba(255, 255, 255, 0.6);
		}
	}

	.button-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;

		@container (min-width: 552px) {
			flex-direction: row;

			> * {
				min-width: 0;
			}
		}
	}
}
</style>
